/**
 * RatingService — client side of in-app store ratings.
 *
 * Wraps `capacitor-rate-app`, which calls the NATIVE in-app review sheets:
 *   - iOS:     SKStoreReviewController.requestReview()
 *   - Android: Google Play In-App Review API (ReviewManager)
 * Both are silent and OS-quota-limited (iOS shows at most ~3 prompts / 365 days
 * per user and decides whether to show it at all; Android has a time-boxed
 * quota). We get NO callback telling us whether the sheet appeared. That is the
 * whole reason eligibility + timing live in the caller (maybeAskForRating in
 * main.ts): we can't ask more, so the only lever is asking the RIGHT user at the
 * RIGHT moment. See the sentiment-funnel flow in main.ts.
 *
 * Compliance: we never reward a rating (Apple 1.1.1 / 3.1, Google policy forbid
 * incentivized reviews) and never put a custom star UI in front of the native
 * sheet. The pre-prompt (RatingPrimer) only asks sentiment and conditionally
 * fires this; it never collects a rating itself.
 *
 * Web/dev is a no-op (the native plugin throws "unimplemented" off-device), so
 * everything is guarded by Capacitor.isNativePlatform(). The funnel UI still
 * runs in the dev browser for testing; only the native call below no-ops.
 */

import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { RateApp } from 'capacitor-rate-app';

/** Play Store package id — known today (set in capacitor.config.ts). */
const ANDROID_PACKAGE = 'com.gritplaygames.streetdicelegends';
/** App Store numeric id (App Store Connect record for Street Dice Legends). */
const IOS_APP_ID = '6780375950';

class RatingServiceImpl {
  private supported(): boolean {
    return Capacitor.isNativePlatform();
  }

  /**
   * Fire the native in-app review sheet. Safe to call from the sentiment-funnel
   * "happy" branch only. No-ops off-device; may also silently no-op on-device if
   * the OS quota is spent — that is expected and not an error.
   */
  async requestReview(): Promise<void> {
    if (!this.supported()) return;
    try {
      await RateApp.requestReview();
    } catch (err) {
      console.warn('[RatingService] requestReview failed:', (err as Error)?.message);
    }
  }

  /**
   * Manual "Rate us" button path: open the store listing's write-review page.
   * Deep-linking the store (unlike the auto-prompt) is allowed and reliable, so
   * a button can use it. iOS without a known app id falls back to the native
   * sheet so the button is never dead.
   */
  async openStoreListing(): Promise<void> {
    const platform = Capacitor.getPlatform();
    let url = '';
    if (platform === 'android') {
      url = `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}`;
    } else if (platform === 'ios') {
      if (IOS_APP_ID) url = `https://apps.apple.com/app/id${IOS_APP_ID}?action=write-review`;
      else { await this.requestReview(); return; }
    }
    if (!url) { await this.requestReview(); return; }
    try {
      await Browser.open({ url });
    } catch (err) {
      console.warn('[RatingService] openStoreListing failed:', (err as Error)?.message);
    }
  }
}

/** App-wide singleton. */
export const ratingService = new RatingServiceImpl();
